import { toast } from "sonner";
import { toastErrorWithHelp } from "@/lib/toastErrorWithHelp";

/** Trigger a browser download of a WAV blob with the ELPM-mix-{stamp}-{target} name. */
function downloadWav(blob, target, suffix = "") {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const dateStamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
  a.href = url;
  a.download = `ELPM-mix-${dateStamp}-${target}${suffix}.wav`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

/**
 * Master bus handlers — extracted verbatim from ELPMStudioDAW.
 * Offline WAV bounce, AI mastering, mastering bypass and the one-click
 * "master + bounce" flow.
 */
export default function useMasteringHandlers({
  engine,
  loadedCount,
  maxDur,
  setBusy,
  masteringTarget,
  setMasteringStats,
}) {
  const handleExport = async () => {
    if (loadedCount === 0) {
      toast.error("Nothing to export", { description: "Load audio into a channel first." });
      return;
    }
    setBusy("export");
    toast.message("Bouncing offline mix to WAV…", {
      description: `${loadedCount} channel${loadedCount === 1 ? "" : "s"} · ${maxDur.toFixed(1)}s · routing through master FX + limiter`,
    });
    try {
      const t0 = performance.now();
      const blob = await engine.exportWav();
      const renderMs = performance.now() - t0;
      const target = engine.lastMastering?.target?.label?.toLowerCase().replace(/\s+/g, "-") || "raw";
      downloadWav(blob, target);
      const sizeMb = (blob.size / 1024 / 1024).toFixed(2);
      const stats = [`${sizeMb} MB`, `${maxDur.toFixed(1)}s @ ${+(engine.ctx.sampleRate / 1000).toFixed(1)} kHz`, `bounced in ${(renderMs / 1000).toFixed(1)}s`];
      if (engine.lastMastering?.after) {
        const m = engine.lastMastering.after;
        stats.push(`${m.lufs.toFixed(1)} LUFS · TP ${m.truePeakDb.toFixed(1)} dB`);
      }
      toast.success("Mix bounced to WAV", {
        description: stats.join(" · "),
        duration: 9000,
      });
    } catch (e) {
      toast.error("Export failed", { description: e.message });
    } finally {
      setBusy(null);
    }
  };

  const handleMaster = async () => {
    if (loadedCount === 0) {
      toast.error("Nothing to master", { description: "Load or record audio into a channel first." });
      return;
    }
    setBusy("master");
    try {
      const stats = await engine.runMastering(masteringTarget);
      setMasteringStats(stats);
      const delta = (stats.after.lufs - stats.before.lufs).toFixed(1);
      toast.success(`Mastered → ${stats.target.label}`, {
        description: `${stats.before.lufs.toFixed(1)} → ${stats.after.lufs.toFixed(1)} LUFS (+${delta} dB) · true peak ${stats.after.truePeakDb.toFixed(1)} dBFS`,
      });
    } catch (e) {
      toastErrorWithHelp("Mastering failed", {
        description: e.message,
        probe: "backend",
      });
    } finally {
      setBusy(null);
    }
  };

  const handleMasteringReset = () => {
    engine.resetMastering();
    setMasteringStats(null);
    toast.message("Mastering bypassed", { description: "Master bus is now flat." });
  };

  /** One-click "ready for streaming" — run AI mastering, then bounce, then download. */
  const handleMasterAndBounce = async () => {
    if (loadedCount === 0) {
      toast.error("Nothing to master + bounce", { description: "Load audio into a channel first." });
      return;
    }
    setBusy("master-and-bounce");
    toast.message("Step 1/2 · AI mastering…", {
      description: `Target: ${masteringTarget.toUpperCase()} · measuring loudness + applying recipe…`,
    });
    let stats = null;
    try {
      stats = await engine.runMastering(masteringTarget);
      setMasteringStats(stats);
      toast.message("Step 2/2 · Bouncing master to WAV…", {
        description: `Mastered to ${stats.after.lufs.toFixed(1)} LUFS · TP ${stats.after.truePeakDb.toFixed(1)} dB · now rendering offline mix`,
      });
      const t0 = performance.now();
      const blob = await engine.exportWav();
      const renderMs = performance.now() - t0;
      const target = stats.target?.label?.toLowerCase().replace(/\s+/g, "-") || masteringTarget;
      downloadWav(blob, target, "-mastered");
      const sizeMb = (blob.size / 1024 / 1024).toFixed(2);
      toast.success(`Mastered + bounced to ${stats.target.label}`, {
        description: `${sizeMb} MB · ${maxDur.toFixed(1)}s @ ${+(engine.ctx.sampleRate / 1000).toFixed(1)} kHz · ${stats.after.lufs.toFixed(1)} LUFS · TP ${stats.after.truePeakDb.toFixed(1)} dB · rendered in ${(renderMs / 1000).toFixed(1)}s`,
        duration: 10000,
      });
    } catch (e) {
      toast.error(stats ? "Bounce failed (mastering succeeded)" : "Master + bounce failed", {
        description: e.message,
      });
    } finally {
      setBusy(null);
    }
  };

  return { handleExport, handleMaster, handleMasteringReset, handleMasterAndBounce };
}
